import { Activity, AlertTriangle, CheckCircle2 } from 'lucide-react';

import { type WidgetComponent, type WidgetMeta } from '../_base/types';
import { WidgetContainer } from '../_base/WidgetContainer';

import { useHealth } from '@/hooks/useHealth';
import { formatRelativeTime } from '@/lib/utils';


const meta: WidgetMeta = {
  id: 'arena-status',
  name: 'Arena',
  description: 'Whether Ugin can see your games.',
  icon: Activity,
  defaultSize: { w: 4, h: 3 },
  category: 'system',
};

/**
 * An empty dashboard looks exactly like a working one, which is the failure
 * mode that cost this project four months. This widget makes the difference
 * visible: what Ugin last saw, and the exact click path when Arena is writing
 * a log with nothing in it.
 */
export const ArenaStatusWidget: WidgetComponent = () => {
  const { data, isLoading, error, refetch } = useHealth();
  const arena = data?.arena ?? null;

  return (
    <WidgetContainer
      meta={meta}
      icon={<Activity className="size-4" />}
      loading={isLoading}
      error={error ?? undefined}
      onRetry={() => void refetch()}
    >
      {arena === null ? (
        <div className="space-y-2 text-sm">
          <p className="font-medium text-ugin-text">Ugin hasn't seen Arena yet.</p>
          <p className="text-ugin-text-muted">
            Start MTG Arena and play a game. The tail runs at logon and posts what it reads within a
            minute.
          </p>
        </div>
      ) : arena.detailedLogs === 'disabled' ? (
        <div className="space-y-2 text-sm" role="status">
          <p className="flex items-center gap-2 font-medium text-ugin-warn">
            <AlertTriangle className="size-4 shrink-0" />
            Detailed Logs is off, so Arena is telling Ugin nothing.
          </p>
          <p className="text-ugin-text-muted">
            Turn it on in Arena:{' '}
            <span className="text-ugin-text">Options → Account → Detailed Logs</span>. Games played
            before then cannot be recovered.
          </p>
        </div>
      ) : (
        <dl className="space-y-2 text-sm">
          <div className="flex items-center gap-2 font-medium text-ugin-success">
            <CheckCircle2 className="size-4 shrink-0" />
            <span>Watching Arena</span>
          </div>
          <Row label="Last seen" value={formatRelativeTime(arena.lastSeenAt)} />
          <Row
            label="Last match"
            value={arena.lastMatchAt === null ? 'none yet' : formatRelativeTime(arena.lastMatchAt)}
          />
          {arena.detailedLogs === 'undetermined' ? (
            <p className="pt-1 text-xs text-ugin-text-faint">
              Arena has not written enough of its log yet to confirm Detailed Logs. Play a game.
            </p>
          ) : null}
        </dl>
      )}
    </WidgetContainer>
  );
};

ArenaStatusWidget.meta = meta;

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-ugin-text-muted">{label}</dt>
      <dd className="font-mono text-xs text-ugin-text">{value}</dd>
    </div>
  );
}
